import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import ElderlyIcon from "@mui/icons-material/Elderly";
import AccessibleIcon from "@mui/icons-material/Accessible";
import HomeIcon from "@mui/icons-material/Home";
import GroupsIcon from "@mui/icons-material/Groups";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import BusinessIcon from "@mui/icons-material/Business";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FloodIcon from "@mui/icons-material/Flood";

interface ProgramModule {
  id: string;
  name: string;
  acronym: string;
  description: string;
  icon: React.ReactNode;
  status: "active" | "available" | "coming_soon";
  features: string[];
  category: string;
}

const programModules: ProgramModule[] = [
  {
    id: "seed",
    name: "Support for Education and Employment Development",
    acronym: "SEED",
    description: "Core case management platform for SEED program with participant tracking, application processing, and benefits management. Additional features available for implementation.",
    icon: <SchoolIcon fontSize="large" />,
    status: "active",
    category: "Education & Employment",
    features: [
      "Participant enrollment and tracking",
      "Case management workflows",
      "Benefit application processing",
      "Document management system",
      "Reporting and analytics dashboard",
    ],
  },
  {
    id: "brisp",
    name: "Building Resilience through Individualized Support Programs",
    acronym: "BRISP",
    description: "Leverage the same proven case management platform for BRISP participants. Configure workflows, forms, and reports specific to resilience-building programs.",
    icon: <GroupsIcon fontSize="large" />,
    status: "active",
    category: "Community Support",
    features: [
      "BRISP-specific participant tracking",
      "Custom workflow configuration",
      "Multi-program enrollment support",
      "Shared reporting infrastructure",
      "Integrated case notes and documentation",
    ],
  },
  {
    id: "ecsp",
    name: "Elderly Care Support Program",
    acronym: "ECSP",
    description: "Dedicated program for senior citizens providing healthcare assistance, pension support, and quality of life services.",
    icon: <ElderlyIcon fontSize="large" />,
    status: "coming_soon",
    category: "Healthcare & Wellness",
    features: [
      "Pension administration",
      "Home care coordination",
      "Healthcare subsidies",
      "Senior activity programs",
      "Caregiver support services",
    ],
  },
  {
    id: "dap",
    name: "Disability Assistance Program",
    acronym: "DAP",
    description: "Comprehensive support for persons with disabilities including financial aid, accessibility services, and vocational training.",
    icon: <AccessibleIcon fontSize="large" />,
    status: "coming_soon",
    category: "Healthcare & Wellness",
    features: [
      "Disability benefits management",
      "Assistive device subsidies",
      "Accessible transport coordination",
      "Vocational rehabilitation",
      "Accessibility advocacy",
    ],
  },
  {
    id: "hsi",
    name: "Housing Support Initiative",
    acronym: "HSI",
    description: "Affordable housing assistance including rent subsidies, home repair grants, and housing counseling services.",
    icon: <HomeIcon fontSize="large" />,
    status: "available",
    category: "Housing & Infrastructure",
    features: [
      "Rental assistance vouchers",
      "Home repair grants",
      "Housing counseling",
      "First-time homebuyer support",
      "Emergency housing placement",
    ],
  },
  {
    id: "ydp",
    name: "Youth Development Program",
    acronym: "YDP",
    description: "Youth-focused initiative providing mentorship, skills training, sports programs, and leadership development opportunities.",
    icon: <GroupsIcon fontSize="large" />,
    status: "coming_soon",
    category: "Education & Employment",
    features: [
      "Mentorship matching",
      "Leadership training",
      "Sports and recreation grants",
      "Youth entrepreneurship support",
      "After-school programs",
    ],
  },
  {
    id: "asp",
    name: "Agricultural Support Program",
    acronym: "ASP",
    description: "Support for farmers and agricultural workers with grants, training, equipment subsidies, and market access assistance.",
    icon: <AgricultureIcon fontSize="large" />,
    status: "coming_soon",
    category: "Economic Development",
    features: [
      "Farming equipment grants",
      "Agricultural training programs",
      "Crop insurance subsidies",
      "Market access support",
      "Sustainable farming incentives",
    ],
  },
  {
    id: "sbdp",
    name: "Small Business Development Program",
    acronym: "SBDP",
    description: "Entrepreneurship support including microloans, business training, mentorship, and market development assistance.",
    icon: <BusinessIcon fontSize="large" />,
    status: "coming_soon",
    category: "Economic Development",
    features: [
      "Microloan administration",
      "Business plan development",
      "Financial literacy training",
      "Entrepreneur mentorship",
      "Market linkage support",
    ],
  },
  {
    id: "hap",
    name: "Healthcare Assistance Program",
    acronym: "HAP",
    description: "Medical expense support for low-income families including prescription subsidies, treatment costs, and preventive care.",
    icon: <LocalHospitalIcon fontSize="large" />,
    status: "coming_soon",
    category: "Healthcare & Wellness",
    features: [
      "Prescription drug subsidies",
      "Medical procedure assistance",
      "Preventive care programs",
      "Chronic disease management",
      "Health insurance support",
    ],
  },
  {
    id: "erp",
    name: "Emergency Relief Program",
    acronym: "ERP",
    description: "Rapid response assistance for emergency situations including natural disasters, economic shocks, and urgent family crises.",
    icon: <WarningAmberIcon fontSize="large" />,
    status: "coming_soon",
    category: "Emergency Services",
    features: [
      "Emergency cash grants",
      "Food and shelter assistance",
      "Crisis counseling referrals",
      "Temporary income support",
      "Rapid needs assessment",
    ],
  },
  {
    id: "egp",
    name: "Education Grants Program",
    acronym: "EGP",
    description: "Comprehensive scholarship and grant system for tertiary education, vocational training, and professional development.",
    icon: <MenuBookIcon fontSize="large" />,
    status: "coming_soon",
    category: "Education & Employment",
    features: [
      "University scholarships",
      "Vocational training grants",
      "Professional certification support",
      "Study abroad assistance",
      "Academic excellence awards",
    ],
  },
  {
    id: "drp",
    name: "Disaster Recovery Program",
    acronym: "DRP",
    description: "Post-disaster assistance for rebuilding homes, businesses, and livelihoods following hurricanes and other natural disasters.",
    icon: <FloodIcon fontSize="large" />,
    status: "coming_soon",
    category: "Emergency Services",
    features: [
      "Home reconstruction grants",
      "Business recovery loans",
      "Temporary housing support",
      "Livelihood restoration",
      "Community rebuilding projects",
    ],
  },
];

const statusConfig = {
  active: { label: "Active", color: "success" as const },
  available: { label: "Available", color: "primary" as const },
  coming_soon: { label: "Coming Soon", color: "warning" as const },
};

export function ProgramModulesPage() {
  const handleContactSales = () => {
    window.open("https://trusttechdigital.com/contact", "_blank");
  };

  const groupedModules = programModules.reduce(
    (acc, module) => {
      if (!acc[module.category]) {
        acc[module.category] = [];
      }
      acc[module.category].push(module);
      return acc;
    },
    {} as Record<string, ProgramModule[]>
  );

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Program Modules
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Modular by design. Expand beyond SEED with BRISP and new Ministry programs as needs grow.
          Each module integrates seamlessly with SEED IMS for unified case management, reporting, and service delivery.
        </Typography>
      </Box>

      {Object.entries(groupedModules).map(([category, modules]) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
            {category}
          </Typography>
          <Grid container spacing={3}>
            {modules.map((module) => {
              const statusConf = statusConfig[module.status];
              return (
                <Grid item xs={12} md={6} lg={4} key={module.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Box sx={{ color: "primary.main" }}>{module.icon}</Box>
                        <Chip label={statusConf.label} color={statusConf.color} size="small" />
                      </Box>

                      <Typography variant="h6" gutterBottom fontWeight={600}>
                        {module.acronym}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" paragraph sx={{ minHeight: 60 }}>
                        {module.description}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Key Features:
                      </Typography>

                      <List dense>
                        {module.features.slice(0, 5).map((feature, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Box sx={{ mt: 2 }}>
                        {module.status === "active" ? (
                          <Chip label="Currently Deployed" color="success" variant="outlined" />
                        ) : (
                          <Button
                            variant={module.status === "available" ? "contained" : "outlined"}
                            fullWidth
                            onClick={handleContactSales}
                          >
                            {module.status === "available" ? "Add Module" : "Request Info"}
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      <Card sx={{ mt: 4, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
        <CardContent sx={{ py: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Need a Custom Module?
              </Typography>
              <Typography variant="body1">
                We can build custom program modules tailored to your Ministry's specific requirements.
                Contact our team to discuss your unique needs and timeline.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleContactSales}
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                Contact TrustTech Digital
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
